/**
 * Global Input Validation Utilities
 * Provides guards for all user inputs across the application
 */

// ============= DATE VALIDATION =============

export interface DateValidationResult {
  isValid: boolean
  error?: string
  date?: Date
}

/**
 * Validates a date string in DD/MM/YYYY format
 * Ensures valid day, month, year ranges and age requirements
 */
export function validateDate(dateStr: string, minAge: number = 13): DateValidationResult {
  // Remove any non-digit characters except slashes
  const cleaned = dateStr.replace(/[^\d/]/g, '')

  // Check format
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(cleaned)) {
    return { isValid: false, error: 'Date must be in DD/MM/YYYY format' }
  }

  const [day, month, year] = cleaned.split('/').map(Number)

  // Validate year range
  const currentYear = new Date().getFullYear()
  if (year < 1900 || year > currentYear) {
    return { isValid: false, error: `Year must be between 1900 and ${currentYear}` }
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Month must be between 01 and 12' }
  }

  // Validate day based on month and year
  const daysInMonth = new Date(year, month, 0).getDate()
  if (day < 1 || day > daysInMonth) {
    return { isValid: false, error: `Day must be between 01 and ${daysInMonth} for month ${month}` }
  }

  // Create date object (month is 0-indexed in JS)
  const date = new Date(year, month - 1, day)

  // Validate age
  const today = new Date()
  const age = today.getFullYear() - date.getFullYear()
  const monthDiff = today.getMonth() - date.getMonth()
  const dayDiff = today.getDate() - date.getDate()

  const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age

  if (actualAge < minAge) {
    return { isValid: false, error: `You must be at least ${minAge} years old` }
  }

  // Check if date is in the future
  if (date > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' }
  }

  return { isValid: true, date }
}

/**
 * Formats a date string as user types, adding slashes automatically
 * @param input - Raw input string
 * @returns Formatted string (DD/MM/YYYY)
 */
export function formatDateInput(input: string): string {
  // Remove all non-digits
  const digits = input.replace(/\D/g, '')

  // Limit to 8 digits (DDMMYYYY)
  const limited = digits.slice(0, 8)

  // Add slashes at appropriate positions
  if (limited.length <= 2) {
    return limited
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`
  }
}

/**
 * Validates day input (01-31)
 */
export function validateDay(day: string): boolean {
  const num = parseInt(day, 10)
  return !isNaN(num) && num >= 1 && num <= 31
}

/**
 * Validates month input (01-12)
 */
export function validateMonth(month: string): boolean {
  const num = parseInt(month, 10)
  return !isNaN(num) && num >= 1 && num <= 12
}

/**
 * Validates year input (1900-current year)
 */
export function validateYear(year: string): boolean {
  const num = parseInt(year, 10)
  const currentYear = new Date().getFullYear()
  return !isNaN(num) && num >= 1900 && num <= currentYear
}

// ============= TEXT VALIDATION =============

/**
 * Validates username format (matches backend rules in auth-api check-username)
 * - 3-20 characters
 * - Lowercase letters, numbers, underscores only
 * - Cannot start or end with underscore
 * - No consecutive underscores
 * - Cannot be all numbers
 * - No 6+ consecutive numbers
 */
export function validateUsername(username: string): { isValid: boolean; error?: string } {
  if (username.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters' }
  }
  if (username.length > 20) {
    return { isValid: false, error: 'Username must be 20 characters or less' }
  }
  if (!/^[a-z0-9_]+$/.test(username)) {
    return { isValid: false, error: 'Only lowercase letters, numbers, and underscores allowed' }
  }
  if (/^_/.test(username)) {
    return { isValid: false, error: 'Username cannot start with an underscore' }
  }
  if (/_$/.test(username)) {
    return { isValid: false, error: 'Username cannot end with an underscore' }
  }
  if (/__+/.test(username)) {
    return { isValid: false, error: 'Username cannot have consecutive underscores' }
  }
  if (/^\d+$/.test(username)) {
    return { isValid: false, error: 'Username cannot be all numbers' }
  }
  if (/\d{6,}/.test(username)) {
    return { isValid: false, error: 'Username cannot have 6+ consecutive numbers' }
  }
  return { isValid: true }
}

/**
 * Validates name (letters, spaces, hyphens, 2-50 chars)
 */
export function validateName(name: string): { isValid: boolean; error?: string } {
  if (name.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters' }
  }
  if (name.length > 50) {
    return { isValid: false, error: 'Name must be less than 50 characters' }
  }
  if (!/^[a-zA-Z\s'-]+$/.test(name)) {
    return { isValid: false, error: 'Name can only contain letters, spaces, hyphens, and apostrophes' }
  }
  return { isValid: true }
}

/**
 * Formats text input by removing invalid characters
 */
export function formatTextInput(input: string, allowedChars: RegExp): string {
  return input.replace(new RegExp(`[^${allowedChars.source}]`, 'g'), '')
}

// ============= PHONE VALIDATION =============

/**
 * Validates phone number (E.164 format)
 */
export function validatePhone(phone: string): { isValid: boolean; error?: string } {
  // Remove all non-digits and plus sign
  const cleaned = phone.replace(/[^\d+]/g, '')

  if (!cleaned.startsWith('+')) {
    return { isValid: false, error: 'Phone must start with country code (e.g., +91)' }
  }

  if (cleaned.length < 10 || cleaned.length > 15) {
    return { isValid: false, error: 'Phone number must be 10-15 digits' }
  }

  return { isValid: true }
}

/**
 * Formats phone input to E.164 format
 */
export function formatPhoneInput(input: string): string {
  // Remove all non-digits except leading plus
  const hasPlus = input.startsWith('+')
  const digits = input.replace(/\D/g, '')
  return hasPlus ? `+${digits}` : digits
}

// ============= EMAIL VALIDATION =============

/**
 * Validates email format
 */
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Invalid email format' }
  }

  if (email.length > 100) {
    return { isValid: false, error: 'Email must be less than 100 characters' }
  }

  return { isValid: true }
}

// ============= NUMBER VALIDATION =============

/**
 * Validates and formats numeric input
 * @param input - Raw input string
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Formatted numeric string
 */
export function formatNumericInput(input: string, min?: number, max?: number): string {
  // Remove all non-digits
  const digits = input.replace(/\D/g, '')

  if (!digits) return ''

  const num = parseInt(digits, 10)

  if (min !== undefined && num < min) return min.toString()
  if (max !== undefined && num > max) return max.toString()

  return num.toString()
}

/**
 * Validates OTP code (6 digits)
 */
export function validateOTP(otp: string): { isValid: boolean; error?: string } {
  if (!/^\d{6}$/.test(otp)) {
    return { isValid: false, error: 'OTP must be 6 digits' }
  }
  return { isValid: true }
}

/**
 * Formats OTP input (digits only, max 6)
 */
export function formatOTPInput(input: string): string {
  return input.replace(/\D/g, '').slice(0, 6)
}
